/**
 * SubunitLabelsOverlay - Renders name labels above subunits in the building editor
 *
 * Shows a small label with subunit name + gear button for each subunit.
 * Clicking the gear icon activates editing (selects subunit + opens properties panel).
 * Labels follow camera movement via map.on('move') subscription.
 */

import { useEffect, useState, useCallback } from 'react'
import { Settings } from 'lucide-react'
import { useSubunitStore } from '@/stores/subunitStore'
import { project3DToScreen, type ScreenPosition } from '@/lib/utils/project-to-screen'
import { getSubunitScenePosition } from '@/lib/utils/subunit-coordinates'
import { SUBUNIT_CATEGORY_COLORS } from '@/types/subunit'
import type { Subunit } from '@/types/subunit'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'

interface SubunitLabelsOverlayProps {
  map: mapboxgl.Map | null
  subunits: Subunit[]
  buildings: Building3DConfigExtended[]
  mapCenter: { lat: number; lng: number } | null
}

interface SubunitLabelPosition {
  subunitId: string
  name: string
  category: string
  x: number
  y: number
  visible: boolean
}

export function SubunitLabelsOverlay({
  map,
  subunits,
  buildings,
  mapCenter,
}: SubunitLabelsOverlayProps) {
  const [labelPositions, setLabelPositions] = useState<SubunitLabelPosition[]>([])
  const selectedSubunitId = useSubunitStore((s) => s.selectedSubunitId)
  const selectSubunit = useSubunitStore((s) => s.selectSubunit)

  const updatePositions = useCallback(() => {
    if (!map || !mapCenter) {
      setLabelPositions([])
      return
    }

    if (subunits.length === 0) {
      setLabelPositions([])
      return
    }

    const positions: SubunitLabelPosition[] = []
    for (const subunit of subunits) {
      const scenePos = getSubunitScenePosition(subunit, buildings)
      if (!scenePos) continue

      const screenPos: ScreenPosition = project3DToScreen(
        [scenePos.x, 0, scenePos.z],
        map,
        mapCenter,
        -25 // small offset — subunits are low-profile
      )

      if (screenPos.visible) {
        positions.push({
          subunitId: subunit.id,
          name: subunit.name,
          category: subunit.category,
          x: screenPos.x,
          y: screenPos.y,
          visible: true,
        })
      }
    }

    setLabelPositions(positions)
  }, [map, mapCenter, buildings, subunits])

  // Subscribe to camera moves
  useEffect(() => {
    if (!map) return

    const handleMove = () => updatePositions()
    map.on('move', handleMove)

    // Initial update
    updatePositions()

    return () => {
      map.off('move', handleMove)
    }
  }, [map, updatePositions])

  // Update when subunits change
  useEffect(() => {
    updatePositions()
  }, [updatePositions])

  if (labelPositions.length === 0) return null

  return (
    <>
      {labelPositions.map((pos) => {
        const isSelected = pos.subunitId === selectedSubunitId
        const categoryColor = SUBUNIT_CATEGORY_COLORS[pos.category as keyof typeof SUBUNIT_CATEGORY_COLORS] ?? '#8B5CF6'
        return (
          <div
            key={pos.subunitId}
            className="absolute z-20 pointer-events-auto"
            style={{
              left: pos.x,
              top: pos.y,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <div
              className={`flex items-center gap-1 p-1 rounded-lg shadow-md backdrop-blur-sm ${
                isSelected
                  ? 'bg-primary/90 text-primary-foreground ring-2 ring-primary'
                  : 'bg-background/90'
              }`}
            >
              {/* Category color dot */}
              <span
                className="w-2 h-2 rounded-full shrink-0 ml-1"
                style={{ backgroundColor: categoryColor }}
              />
              <span className="px-1 py-0.5 text-[10px] font-medium truncate max-w-[100px]">
                {pos.name}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  selectSubunit(pos.subunitId)
                }}
                className={`flex items-center justify-center w-4 h-4 rounded hover:bg-accent/50 ${
                  isSelected ? 'hover:bg-primary-foreground/20' : ''
                }`}
                title="Edit subunit"
              >
                <Settings className="w-2.5 h-2.5" />
              </button>
            </div>
          </div>
        )
      })}
    </>
  )
}
